<script>
    import { getContext } from 'svelte';
    import { Policy } from "@Store";
    import Question from "@components/Question.svelte";
    import 'animate.css';
    import { computeDirectorsAndOfficersValid } from '@routes/validators/validator';
    const CONTEXT = getContext('CONTEXT');

    $: questions = $Policy.questions ? $Policy.questions : [];

    // Create a lookup map in one pass - much more efficient!
    $: questionMap = questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});

    // Now you can access any question efficiently
    $: ({
        'Do you require Directors and Officers Insurance?': directors_and_officers_liability_question,
        'Directors & Officers Liability Limit Requested': directors_and_officers_liability_limit_requested_question,
        'Are you in arrears with CRA?': are_you_in_arrears_with_cra_question,
        'Any breach of contracts/debts in the last 3 years?': any_breach_of_contracts_debts_in_the_last_3_years_question,
        'Is the organization solvent?': is_the_organization_solvent_question,
        'Have you experienced high turnover in the past 2 years?': have_you_experienced_high_turnover_in_the_past_2_years_question,
        'Do you have expected layoffs/closures?': do_you_have_expected_layoffs_closures_question,
        'Do you have written hiring/interview policies?': do_you_have_written_hiring_interview_policies_question,
        'Do you have a human resources department?': do_you_have_a_human_resources_department_question,
        'Do you have written discipline/termination policies?': do_you_have_written_discipline_termination_policies_question,
        'Insurer (D&O)': insurer_d_o_question,
        'Expiry Date (D&O)': expiry_date_d_o_question,
        'Retroactive Date (if applicable, if date is unknown, please supply date licensed as an ND) (D&O)': retroactive_date_d_o_question,
    } = questionMap);

    // Create named values lookup map - ONE iteration instead of 9!
    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    // Helper function to check if a named value exists and is not empty
    $: getAnswerText = (q) => (q && $Policy.answers?.[q.id]?.text_answer || '').trim();
    $: isAnsweredById = (q) => !!getAnswerText(q);

    // Branching flags for the first question
    $: do_not_need_d_o = getAnswerText(directors_and_officers_liability_question).toLowerCase().includes('no');
    $: needs_d_o = directors_and_officers_liability_question_answered && !do_not_need_d_o;

    // Now efficiently check all values
    $: directors_and_officers_liability_question_answered = isAnsweredById(directors_and_officers_liability_question);


    $: directors_and_officers_liability_valid = computeDirectorsAndOfficersValid($Policy);

    // Update CONTEXT when validation changes
    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            directorsandofficersliability: directors_and_officers_liability_valid
        }));
    }

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e);
    }


</script>

<div class="page-container">
    <div class="questions-wrapper">
        <!-- APPLICANT QUESTIONS -->
        {#if directors_and_officers_liability_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={directors_and_officers_liability_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}

        {#if needs_d_o}
            {#if directors_and_officers_liability_limit_requested_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={directors_and_officers_liability_limit_requested_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}

            {#if are_you_in_arrears_with_cra_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={are_you_in_arrears_with_cra_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if any_breach_of_contracts_debts_in_the_last_3_years_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={any_breach_of_contracts_debts_in_the_last_3_years_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if is_the_organization_solvent_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={is_the_organization_solvent_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if have_you_experienced_high_turnover_in_the_past_2_years_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={have_you_experienced_high_turnover_in_the_past_2_years_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if do_you_have_written_hiring_interview_policies_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={do_you_have_written_hiring_interview_policies_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if do_you_have_a_human_resources_department_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={do_you_have_a_human_resources_department_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if do_you_have_written_discipline_termination_policies_question}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={do_you_have_written_discipline_termination_policies_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if insurer_d_o_question && expiry_date_d_o_question && retroactive_date_d_o_question}
                <div class="section-container animate__animated animate__fadeInUp">
                    <div class="section-header">
                        <div class="section-icon">🏢</div>
                        <h1>Previous D&O Insurance</h1>
                    </div>
                    
                    <div class="sub-questions">
                        <div class="sub-question">
                            <Question question={insurer_d_o_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>

                        <div class="sub-question">
                            <Question question={expiry_date_d_o_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                        <div class="sub-question">
                            <Question question={retroactive_date_d_o_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    </div>
                </div>
            {/if}

        {/if}

    </div>
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }

    /* Section Container Styles */
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }

    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .sub-question {
        position: relative;
    }

    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }

        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }

        .sub-question {
            padding-left: 1rem;
        }

        .sub-question::before {
            width: 3px;
        }
    }
</style>